import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useBottomAligned } from './useBottomAligned';
import { QcsFullscreenModal } from '../common/basic/QcsFullscreenModal';
import { QcsButton } from '../common/basic/QcsButton';
import { FetchState } from '../../store/fetchState';
import { AnalyticsFlag } from '@api/models/analytics/GroupingInfo';
import { AnalyticsKpiStrip, AnalyticsResultToolbar } from './components';
import { ColumnPicker } from './components/ColumnPicker';
import { ExplorerBody, ExplorerBodyProps } from './components/ExplorerBody';
import { ExplorerFilterForm } from './components/ExplorerFilterForm';
import { useHintDismissed } from '../../hooks/useHintDismissed';
import { TileDialog } from './components/TileDialog';
import { ScopeBadge } from './components/ScopeBadge';
import { TileEditingActions, TileEditingBanner, TileEditingTitle } from './components/TileDialogStyles';
import { DrillDownBadge, ExplorerFilterPanel, ExplorerLayout, FadeIn, StepBody } from './AnalyticsStyles';
import { AnalyticsDomainConfig, NO_GROUP_BY } from './analyticsDomainConfig';
import { ExplorationConfig, ExplorationStateSnapshot, useAnalyticsExploration } from './useAnalyticsExploration';
import { useAnalyticsSearchParams } from './useAnalyticsSearchParams';
import { TILE_DIALOG_CHART_TYPES, useTileEditing } from './useTileEditing';
import { useAppSelector } from '../../store';
import { selectIdentityRoles } from '../../store/entities/identity';
import { hasRole } from '../../utils/roles';
import { UserRole } from '@api/models/enums/UserRole';

interface Props {
  config: AnalyticsDomainConfig;
  initialConfig?: ExplorationConfig;
  onBackToOverview?: () => void;
  onStateChange?: (state: ExplorationStateSnapshot) => void;
}

export const AnalyticsExplorer: FC<Props> = ({ config, initialConfig, onBackToOverview, onStateChange }) => {
  const { t } = useTranslation();
  const identityRoles = useAppSelector(selectIdentityRoles);
  const isAdmin = hasRole(identityRoles, [UserRole.AdminQcs]);
  const { readFromUrl } = useAnalyticsSearchParams();
  // Seed exploration state from URL on first render so deep links render
  // immediately with the right values instead of flashing defaults.
  const urlSeed = readFromUrl(config);
  const exploration = useAnalyticsExploration(config, onStateChange, urlSeed);
  const lastConfigRef = useRef<ExplorationConfig | undefined>(undefined);
  const didInitRef = useRef(false);
  const [fullscreen, setFullscreen] = useState(false);
  const openFullscreen = useCallback(() => setFullscreen(true), []);
  const closeFullscreen = useCallback(() => setFullscreen(false), []);

  const filterPanelRef = useBottomAligned(true);

  const { formValues, groupBy, view, chartKind, yKey, drillDownContext, isDrillDown, activeEntry, groupByOptions, visibleColumns } = exploration;

  const totalsPresent = !!activeEntry.data?.totals;
  const responseColumns = useMemo(() => activeEntry.data?.columns ?? [], [activeEntry.data]);
  const tile = useTileEditing(config, { formValues, groupBy, chartKind, yKey, view, totalsPresent, responseColumns }, onBackToOverview);

  /**
   * Initializes exploration state when a new config arrives (e.g. tile click),
   * clears tile-editing context when the parent drops the config, or falls back
   * to URL/defaults on first mount. The lastConfigRef guard prevents
   * re-initialization when the same config object is received across re-renders.
   */
  useEffect(() => {
    if (initialConfig && initialConfig !== lastConfigRef.current) {
      lastConfigRef.current = initialConfig;
      didInitRef.current = true;
      exploration.initialize(initialConfig);
      tile.setEditingTileId(initialConfig.tileId);
      tile.setEditingTileTitle(initialConfig.tileTitle);
      tile.setEditingTileScope(tile.activeScope);
      tile.setEditingTileChartType(initialConfig.tileChartType);
      tile.setEditingTileMetric(initialConfig.tileMetric);
    } else if (!initialConfig && lastConfigRef.current) {
      lastConfigRef.current = undefined;
      tile.handleClearEditingContext();
    } else if (!didInitRef.current) {
      didInitRef.current = true;
      const url = readFromUrl(config);
      if (url) {
        exploration.initialize(
          { groupBy: url.groupBy, formValues: url.formValues },
          {
            view: url.view,
            chartKind: url.chartKind,
            yKey: url.yKey,
            drillDownContext: url.drillDownContext,
            visibleColumns: url.visibleColumns,
            skipStateChange: true,
          },
        );
      } else {
        exploration.initialize();
      }
    }
  }, [
    initialConfig,
    exploration,
    tile.activeScope,
    tile.setEditingTileId,
    tile.setEditingTileTitle,
    tile.setEditingTileScope,
    tile.setEditingTileChartType,
    tile.setEditingTileMetric,
    tile.handleClearEditingContext,
    readFromUrl,
    config,
  ]);

  const loading = activeEntry.state === FetchState.Loading;
  const hasError = activeEntry.state === FetchState.Error;
  const data = activeEntry.data;

  const currentGroupBy = isDrillDown ? NO_GROUP_BY : groupBy;
  const chartGroupByOptions = useMemo(() => groupByOptions.filter((o) => o.value !== NO_GROUP_BY), [groupByOptions]);

  const [hintDismissed, dismissNotice] = useHintDismissed('analytics-notice');
  const [noticeRestored, setNoticeRestored] = useState(false);
  const noticeDismissed = hintDismissed && !noticeRestored;
  const knownFlags = useMemo(() => new Set<AnalyticsFlag>(Object.values(AnalyticsFlag)), []);
  const noticeFlags = useMemo(
    () => (data?.grouping?.flags ?? []).filter((f): f is AnalyticsFlag => knownFlags.has(f as AnalyticsFlag)),
    [data?.grouping?.flags, knownFlags],
  );

  const handleNoticeDismiss = useCallback(() => {
    dismissNotice();
    setNoticeRestored(false);
  }, [dismissNotice]);

  const handleExport = useMemo(() => {
    if (!config.fetchExport) return undefined;
    // Resolve the effective on-screen columns so the export matches what the user sees:
    // user's ordered selection if any, otherwise the metadata defaults.
    const effectiveKeys: string[] = (() => {
      const known = new Set(responseColumns.map((c) => c.key));
      const fromUser = visibleColumns.filter((k) => known.has(k));
      if (fromUser.length > 0) return fromUser;
      return responseColumns.filter((c) => c.defaultVisible).map((c) => c.key);
    })();
    return (format?: string, scope?: 'page' | 'all') =>
      config.fetchExport!({
        ...config.toQueryParams(formValues, currentGroupBy),
        format,
        paged: scope === 'page',
        columns: effectiveKeys.length > 0 ? effectiveKeys.join(',') : undefined,
      });
  }, [config, formValues, currentGroupBy, visibleColumns, responseColumns]);

  const bodyProps: ExplorerBodyProps = {
    config,
    data,
    loading,
    hasError,
    view,
    chartKind,
    yKey,
    groupBy: currentGroupBy,
    chartGroupByOptions,
    noticeFlags,
    noticeDismissed,
    rowsPerPage: exploration.pageSize,
    rowsPerPageDisabled: exploration.savingPageSize,
    visibleColumns,
    onNoticeDismiss: handleNoticeDismiss,
    onGroupByChange: exploration.changeGroupBy,
    onYKeyChange: exploration.setYKey,
    onChartKindChange: exploration.setChartKind,
    onDrillDown: exploration.drillDown,
    onPageChange: exploration.changePage,
    onPageSizeChange: exploration.changePageSize,
  };

  const FilterBar = config.FilterBarComponent;

  return (
    <FadeIn>
      <ExplorerLayout>
        <ExplorerFilterPanel ref={filterPanelRef}>
          <Formik
            initialValues={formValues}
            enableReinitialize={false}
            onSubmit={exploration.applyFilters}
            validate={config.validate ? (values) => config.validate!(values, t) : undefined}
          >
            <ExplorerFilterForm formValues={formValues} FilterBar={FilterBar} />
          </Formik>
        </ExplorerFilterPanel>

        <StepBody>
          {tile.editingTileId && tile.editingTileTitle && (
            <TileEditingBanner>
              <span>{t('analytics.tile.editing')}:</span>
              <TileEditingTitle>{tile.editingTileTitle}</TileEditingTitle>
              <ScopeBadge scope={tile.editingTileScope} />
              <TileEditingActions>
                <QcsButton variant="contained" sx={{ fontSize: '0.8rem', padding: '4px 12px' }} onClick={tile.handleOpenEditTileDialog}>
                  {t('analytics.tile.update')}
                </QcsButton>
                <QcsButton variant="text" sx={{ fontSize: '0.8rem', padding: '4px 12px' }} color="error" onClick={tile.handleDeleteConfirm}>
                  {t('analytics.tile.delete')}
                </QcsButton>
              </TileEditingActions>
            </TileEditingBanner>
          )}

          {drillDownContext && (
            <DrillDownBadge>
              <button type="button" onClick={exploration.clearDrillDown}>
                <ArrowBackIcon />
                {t('analytics.drillDown.clear')}
              </button>
              <span className="drill-separator" aria-hidden>
                ·
              </span>
              <span>{t('analytics.drillDown.badge', { label: drillDownContext.label })}</span>
            </DrillDownBadge>
          )}

          <AnalyticsKpiStrip data={data} loading={loading} />

          <AnalyticsResultToolbar
            groupBy={currentGroupBy}
            groupByOptions={groupByOptions}
            onGroupByChange={exploration.changeGroupBy}
            view={view}
            onViewChange={exploration.setView}
            onExport={handleExport}
            exportOptions={config.exportOptions}
            paginated={exploration.isRowsMode}
            isDrillDown={isDrillDown}
            onFullscreen={openFullscreen}
            onSaveAsTile={tile.canSaveAsTile ? tile.handleOpenCreateTileDialog : undefined}
            hasNotice={noticeFlags.length > 0}
            noticeDismissed={noticeDismissed}
            onNoticeRestore={() => setNoticeRestored(true)}
            columnPicker={
              responseColumns.length > 0 ? (
                <ColumnPicker
                  columns={responseColumns}
                  visibleColumns={visibleColumns}
                  groupColumnLabelKey={groupByOptions.find((o) => o.value === currentGroupBy)?.labelKey}
                  onChange={exploration.setVisibleColumns}
                />
              ) : undefined
            }
          />

          <ExplorerBody {...bodyProps} />
        </StepBody>
      </ExplorerLayout>

      <TileDialog
        key={tile.tileDialogMode === 'edit' ? `edit:${tile.editingTileId ?? ''}` : 'create'}
        open={tile.tileDialogOpen}
        mode={tile.tileDialogMode}
        initialTitle={tile.tileDialogMode === 'edit' ? (tile.editingTileTitle ?? '') : ''}
        config={tile.tileDialogConfig}
        availableChartTypes={TILE_DIALOG_CHART_TYPES}
        saving={tile.tileDialogSaving}
        showTargetSelector={isAdmin}
        defaultScope={tile.tileDialogMode === 'edit' ? tile.editingTileScope : tile.activeScope}
        onClose={() => tile.setTileDialogOpen(false)}
        onConfirm={tile.handleTileDialogConfirm}
      />

      <QcsFullscreenModal
        open={fullscreen}
        onClose={closeFullscreen}
        contentProps={{ 'data-analytics-fullscreen': true }}
        disablePadding={view === 'table'}
      >
        <ExplorerBody {...bodyProps} hideNotice />
      </QcsFullscreenModal>
    </FadeIn>
  );
};
